import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { DeviceCredentials } from '@/types/player.types'

// Armazena hardwareId em localStorage separadamente, com uma chave que não será limpada
const HARDWARE_ID_KEY = 'castiq-player-hardware-id'

function getPersistentHardwareId(): string | null {
  try {
    return localStorage.getItem(HARDWARE_ID_KEY)
  } catch {
    return null
  }
}

function savePersistentHardwareId(id: string): void {
  try {
    localStorage.setItem(HARDWARE_ID_KEY, id)
  } catch {
    // Falha silenciosa se localStorage não estiver disponível
  }
}

interface AuthState {
  credentials: DeviceCredentials | null
  hardwareId: string | null
  setCredentials: (creds: DeviceCredentials) => void
  setPairedDeviceId: (deviceId: string, deviceToken: string) => void
  clearCredentials: () => void
  ensureHardwareId: () => string
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      credentials: null,
      hardwareId: getPersistentHardwareId(),

      setCredentials: (credentials) => set({ credentials }),

      // Chamado logo após o status 'paired' — salva deviceId+token antes de buscar workspace info
      setPairedDeviceId: (deviceId, deviceToken) =>
        set((s) => ({
          credentials: s.credentials
            ? { ...s.credentials, deviceId, deviceToken }
            : { deviceId, deviceToken, workspaceId: '', workspaceName: '', workspaceTimezone: 'UTC' }
        })),

      clearCredentials: () => set({ credentials: null }),

      ensureHardwareId: () => {
        // Primeiro tenta obter do estado (em memória)
        let existing = get().hardwareId

        // Se não existe em memória, tenta obter do localStorage isolado
        if (!existing) {
          existing = getPersistentHardwareId()
        }

        // Se ainda não existe, gera um novo
        if (!existing) {
          existing = crypto.randomUUID()
        }

        // Salva tanto no store quanto no localStorage isolado
        set({ hardwareId: existing })
        savePersistentHardwareId(existing)

        return existing
      }
    }),
    { name: 'castiq-player-auth' }
  )
)
